import { RotateCw } from 'lucide-react';

import { Button } from '@/components/ui/button';

/**
 * What a media block shows when its clip or image is not there — which is not "nothing".
 *
 * Both media blocks rendered `data.assetId && cachedAsset ? <player/> : null` in Preview, so a
 * block whose asset had not arrived was ABSENT: no space, no message, no trace. Entering Preview
 * remounts every block, so the fetch restarts every time and the block is always missing for a
 * moment; when the fetch fails it is missing for good. Reported as a sound block that "did not
 * mount on a few occasions", which is exactly the shape of an intermittent request.
 *
 * Silent was the worst of the available behaviours. A teacher sees a task that is short by one
 * block and no reason anywhere, and the `catch` that would have explained it sets a toast the
 * absent block cannot render. The collapsed card had a loading state all along — Preview simply
 * never got one.
 *
 * Holds the height the player will take, so nothing below jumps when the asset lands.
 */
export function MediaPlaceholder({ message, failed, retryLabel, onRetry, boxClass, boxStyle }: {
  message: string;
  /**
   * The box the real media occupies, so the page does not move when it arrives.
   *
   * Without it this stood 64px tall where an image stands 256, so every gap — first load, a hop
   * between tasks, Preview's Reset — collapsed the block by 192px and sprang back. On a page near
   * the scroll threshold that toggles the scrollbar, and the jump reads as the content stretching.
   *
   * This used to say Sound could pass nothing because its player is about 64px tall already. It
   * is not: measured, the collapsed card's player is 150px and Preview's is 174, so Sound was
   * leaving the same hole Image was. It passes `boxStyle` now, from `playerBoxHeight`.
   */
  boxClass?: string;
  /**
   * The same reservation as a pixel height, for media whose size is computed rather than named by
   * a class. Sound's player height depends on its props (waveform height, whether the speed row
   * is shown), so there is no one class that describes it.
   */
  boxStyle?: React.CSSProperties;
  /** A failure, not a wait: spoken at once, coloured as a warning, and offered a way out. */
  failed?: boolean;
  retryLabel?: string;
  onRetry?: () => void;
}) {
  return (
    <div
      style={boxStyle}
      className={`flex w-full flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-ws-border bg-ws-btn px-3 py-4 ${boxClass ?? (boxStyle ? '' : 'min-h-16')}`}
    >
      <p
        // A wait is a status and a failure is an alert: one is worth interrupting for, the other is
        // worth mentioning when the reader gets there.
        role={failed ? 'alert' : 'status'}
        className={failed ? 'text-xs text-ws-warning-fg' : 'text-xs italic text-ws-muted'}
      >
        {message}
      </p>
      {failed && onRetry && retryLabel && (
        // The retry the block already had and could not reach: `refreshCachedAsset` is wired to the
        // player as `onRetry`, and the player is precisely what does not render when the asset
        // never arrived. A signed URL that expired mid-session needs a re-fetch, not a reload.
        <Button size='sm' icon={<RotateCw />} onClick={onRetry}>{retryLabel}</Button>
      )}
    </div>
  );
}
